import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="store-search"
//
// Every in-page search goes through requestSubmit() rather than assigning
// frame.src. A frame navigation is only sent with an Accept header that allows
// turbo-stream when the navigation element opts in, so driving the frame
// directly would skip index.turbo_stream.erb and leave the #search_status live
// region stale.
export default class extends Controller {
  static targets = ["input", "spinner", "buttonText"]

  connect() {
    this.suppressHistory = false
    this.handlePopState = this.handlePopState.bind(this)

    // No frame listeners: the stream response replaces the frame element, so a
    // stored reference goes stale after the first search. turbo:submit-start /
    // turbo:submit-end on the form (see the view's data-action) cover every
    // path, including popstate, because each one submits the form.
    window.addEventListener("popstate", this.handlePopState)
  }

  disconnect() {
    window.removeEventListener("popstate", this.handlePopState)
  }

  choose(event) {
    event.preventDefault()

    if (this.hasInputTarget) {
      this.inputTarget.value = event.params.query
      this.inputTarget.focus()
    }

    this.element.requestSubmit()
  }

  showLoading() {
    if (!this.suppressHistory) this.updateLocation(this.currentQuery())
    this.setLoading(true)
  }

  hideLoading() {
    this.suppressHistory = false
    this.setLoading(false)
  }

  currentQuery() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : ""
  }

  updateLocation(query) {
    const url = new URL(window.location.href)
    const actionUrl = new URL(this.element.action, window.location.origin)

    url.pathname = actionUrl.pathname
    if (query) {
      url.searchParams.set("q", query)
    } else {
      url.searchParams.delete("q")
    }

    if (url.toString() !== window.location.href) {
      window.history.pushState({}, "", url.toString())
    }
  }

  handlePopState() {
    const query = new URL(window.location.href).searchParams.get("q") || ""

    if (this.hasInputTarget) this.inputTarget.value = query

    // Going back to a URL whose q is below minlength leaves the form invalid,
    // and requestSubmit() would silently do nothing. Bail before flagging the
    // history suppression, or it would leak into the next real submission and
    // stop that one from updating the URL.
    if (!this.element.checkValidity()) return

    // Re-running the search must not push the state we just popped. The spinner
    // is left to showLoading() on turbo:submit-start, so a submission that never
    // starts cannot strand it.
    this.suppressHistory = true
    this.element.requestSubmit()
  }

  setLoading(isLoading) {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("d-none", !isLoading)
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = isLoading ? "Searching" : "Search"
    }
  }
}
